// Daily puzzle generator that creates consistent puzzles based on date
// This ensures all users get the same puzzle each day

export interface DailyPuzzleConfig {
  date: string; // YYYY-MM-DD format
  seed: number;
}

// Simple seeded random number generator
class SeededRandom {
  private seed: number;

  constructor(seed: number) {
    this.seed = seed;
  }

  // Park-Miller PRNG
  next(): number {
    this.seed = (this.seed * 16807) % 2147483647;
    return (this.seed - 1) / 2147483646;
  }

  // Random integer between min and max (inclusive)
  nextInt(min: number, max: number): number {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }

  // Shuffle array using Fisher-Yates algorithm
  shuffle<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = this.nextInt(0, i);
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }
}

// Convert date to seed number
export const dateToSeed = (date: Date): number => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 0-based to 1-based
  const day = date.getDate();
  
  // Create a unique seed based on date
  // Using a large prime and mixing date components
  return (year * 10000 + month * 100 + day) * 137 + 12345;
};

// Get today's date in YYYY-MM-DD format
export const getTodayDateString = (): string => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Get daily puzzle configuration
export const getDailyPuzzleConfig = (gameType: string): DailyPuzzleConfig => {
  const today = new Date();
  const dateString = getTodayDateString();
  
  // Create different seeds for different game types on the same day
  const baseSeed = dateToSeed(today);
  const gameTypeSeed = gameType.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const seed = baseSeed + gameTypeSeed;
  
  return {
    date: dateString,
    seed: seed
  };
};

// Create seeded random instance for daily puzzles
export const createDailyRandom = (gameType: string): SeededRandom => {
  const config = getDailyPuzzleConfig(gameType);
  return new SeededRandom(config.seed);
};

// Generate a daily sum grid puzzle
// Practice mode generators (random seed each time)
export const generatePracticeSumGrid = (): {
  grid: (number | null)[][],
  rowSums: number[],
  colSums: number[],
  solution: number[][],
  givenCells: {row: number, col: number}[]
} => {
  const random = new SeededRandom(Math.floor(Math.random() * 1000000));
  return generateSumGridWithRandom(random);
};

export const generateDailySumGrid = (): {
  grid: (number | null)[][],
  rowSums: number[],
  colSums: number[],
  solution: number[][],
  givenCells: {row: number, col: number}[]
} => {
  const random = createDailyRandom('sum-grid');
  return generateSumGridWithRandom(random);
};

const generateSumGridWithRandom = (random: SeededRandom): {
  grid: (number | null)[][],
  rowSums: number[],
  colSums: number[],
  solution: number[][],
  givenCells: {row: number, col: number}[]
} => {
  
  // Create a valid solution using numbers 1-9
  const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const shuffled = random.shuffle(numbers);
  
  // Place numbers in 3x3 grid
  const solution: number[][] = [
    [shuffled[0], shuffled[1], shuffled[2]],
    [shuffled[3], shuffled[4], shuffled[5]],
    [shuffled[6], shuffled[7], shuffled[8]]
  ];
  
  // Calculate row and column sums
  const rowSums = solution.map(row => row.reduce((sum, num) => sum + num, 0));
  const colSums = [0, 1, 2].map(col => solution.reduce((sum, row) => sum + row[col], 0));
  
  // Select exactly 3 cells to be given as hints
  const numGivenCells = 3;
  const allPositions = [];
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 3; col++) {
      allPositions.push({row, col});
    }
  }
  
  const givenCells = random.shuffle(allPositions).slice(0, numGivenCells);
  
  // Create the initial grid with given cells
  const grid: (number | null)[][] = [
    [null, null, null],
    [null, null, null],
    [null, null, null]
  ];
  
  givenCells.forEach(({row, col}) => {
    grid[row][col] = solution[row][col];
  });
  
  return {
    grid,
    rowSums,
    colSums,
    solution,
    givenCells
  };
};

// Practice mode multiplication grid
export const generatePracticeMultiplicationGrid = (): {
  grid: (number | null)[][],
  rowProducts: number[],
  colProducts: number[],
  solution: number[][],
  givenCells: {row: number, col: number}[]
} => {
  const random = new SeededRandom(Math.floor(Math.random() * 1000000));
  return generateMultiplicationGridWithRandom(random);
};

// Generate a daily multiplication grid puzzle
export const generateDailyMultiplicationGrid = (): {
  grid: (number | null)[][],
  rowProducts: number[],
  colProducts: number[],
  solution: number[][],
  givenCells: {row: number, col: number}[]
} => {
  const random = createDailyRandom('multiplication-grid');
  return generateMultiplicationGridWithRandom(random);
};

const generateMultiplicationGridWithRandom = (random: SeededRandom): {
  grid: (number | null)[][],
  rowProducts: number[],
  colProducts: number[],
  solution: number[][],
  givenCells: {row: number, col: number}[]
} => {
  
  // For multiplication grids, we need to be more careful about the numbers
  // to ensure we get reasonable products
  const possibleNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  let attempts = 0;
  let solution: number[][];
  let rowProducts: number[];
  let colProducts: number[];
  
  do {
    const shuffled = random.shuffle([...possibleNumbers]);
    solution = [
      [shuffled[0], shuffled[1], shuffled[2]],
      [shuffled[3], shuffled[4], shuffled[5]],
      [shuffled[6], shuffled[7], shuffled[8]]
    ];
    
    rowProducts = solution.map(row => row.reduce((product, num) => product * num, 1));
    colProducts = [0, 1, 2].map(col => solution.reduce((product, row) => product * row[col], 1));
    
    // Check if products are reasonable (not too large)
    const maxProduct = Math.max(...rowProducts, ...colProducts);
    if (maxProduct <= 1000) break;
    
    attempts++;
  } while (attempts < 100);
  
  // Select exactly 3 cells to be given as hints
  const numGivenCells = 3;
  const allPositions = [];
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 3; col++) {
      allPositions.push({row, col});
    }
  }
  
  const givenCells = random.shuffle(allPositions).slice(0, numGivenCells);
  
  // Create the initial grid with given cells
  const grid: (number | null)[][] = [
    [null, null, null],
    [null, null, null],
    [null, null, null]
  ];
  
  givenCells.forEach(({row, col}) => {
    grid[row][col] = solution[row][col];
  });
  
  return {
    grid,
    rowProducts,
    colProducts,
    solution,
    givenCells
  };
};

// Practice mode combined grid
export const generatePracticeCombinedGrid = (): {
  grid: (number | null)[][],
  rowSums: number[],
  colSums: number[],
  rowProducts: number[],
  colProducts: number[],
  solution: number[][],
  givenCells: {row: number, col: number}[]
} => {
  const random = new SeededRandom(Math.floor(Math.random() * 1000000));
  return generateCombinedGridWithRandom(random);
};

// Generate a daily combined grid puzzle (both sum and product constraints)
export const generateDailyCombinedGrid = (): {
  grid: (number | null)[][],
  rowSums: number[],
  colSums: number[],
  rowProducts: number[],
  colProducts: number[],
  solution: number[][],
  givenCells: {row: number, col: number}[]
} => {
  const random = createDailyRandom('combined-grid');
  return generateCombinedGridWithRandom(random);
};

const generateCombinedGridWithRandom = (random: SeededRandom): {
  grid: (number | null)[][],
  rowSums: number[],
  colSums: number[],
  rowProducts: number[],
  colProducts: number[],
  solution: number[][],
  givenCells: {row: number, col: number}[]
} => {
  
  // This is the most complex puzzle type
  // We need to find arrangements that satisfy both sum and product constraints
  const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  let attempts = 0;
  let solution: number[][];
  let rowSums: number[];
  let colSums: number[];
  let rowProducts: number[];
  let colProducts: number[];
  
  do {
    const shuffled = random.shuffle([...numbers]);
    solution = [
      [shuffled[0], shuffled[1], shuffled[2]],
      [shuffled[3], shuffled[4], shuffled[5]],
      [shuffled[6], shuffled[7], shuffled[8]]
    ];
    
    rowSums = solution.map(row => row.reduce((sum, num) => sum + num, 0));
    colSums = [0, 1, 2].map(col => solution.reduce((sum, row) => sum + row[col], 0));
    rowProducts = solution.map(row => row.reduce((product, num) => product * num, 1));
    colProducts = [0, 1, 2].map(col => solution.reduce((product, row) => product * row[col], 1));
    
    // Check if products are reasonable (not too large)
    const maxProduct = Math.max(...rowProducts, ...colProducts);
    if (maxProduct <= 500) break; // Lower threshold for combined puzzles
    
    attempts++;
  } while (attempts < 100);
  
  // Select 0 cells to be given as hints (no starting boxes for combo grid)
  const numGivenCells = 0;
  const allPositions = [];
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 3; col++) {
      allPositions.push({row, col});
    }
  }
  
  const givenCells = random.shuffle(allPositions).slice(0, numGivenCells);
  
  // Create the initial grid with given cells
  const grid: (number | null)[][] = [
    [null, null, null],
    [null, null, null],
    [null, null, null]
  ];
  
  givenCells.forEach(({row, col}) => {
    grid[row][col] = solution[row][col];
  });
  
  return {
    grid,
    rowSums,
    colSums,
    rowProducts,
    colProducts,
    solution,
    givenCells
  };
};